import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ComposedChart, Bar, Area } from 'recharts';

interface PredictiveAnalyticsDashboardProps {
  data: {
    success_probability: Array<{
      workshop_id: string;
      title: string;
      current_progress: number;
      success_probability: number;
      risk_factors: string[];
      completion_forecast: string;
    }>;
    resource_forecast: Array<{
      month: string;
      mentor_demand: number;
      mentor_availability: number;
      workshop_capacity: number;
      resource_gap: number;
    }>;
    performance_trends: Array<{
      month: string;
      actual_completion: number;
      predicted_completion: number;
      confidence_interval_upper: number;
      confidence_interval_lower: number;
    }>;
    risk_assessment: {
      high_risk_workshops: number;
      medium_risk_workshops: number;
      low_risk_workshops: number;
      critical_issues: Array<{
        issue: string;
        impact: number;
        probability: number;
        mitigation: string;
      }>;
    };
    recommendations: Array<{
      category: string;
      priority: 'high' | 'medium' | 'low';
      recommendation: string;
      expected_impact: number;
      implementation_effort: 'low' | 'medium' | 'high';
    }>;
  };
  loading?: boolean;
}

const PredictiveAnalyticsDashboard: React.FC<PredictiveAnalyticsDashboardProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Predictive Analytics Dashboard</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Predictive Analytics Dashboard</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No predictive analytics data available
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getRiskLevel = (probability: number) => {
    if (probability >= 80) return { level: 'High Risk', color: '#ef4444' };
    if (probability >= 60) return { level: 'Medium Risk', color: '#f59e0b' };
    if (probability >= 40) return { level: 'Low Risk', color: '#10b981' };
    return { level: 'Very Low Risk', color: '#6b7280' };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Predictive Analytics Dashboard</h3>
      
      {/* Success Probability Scatter Plot */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Workshop Success Probability Analysis</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={data.success_probability} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="current_progress" 
                name="Current Progress"
                tick={{ fontSize: 12 }}
                label={{ value: 'Current Progress (%)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                dataKey="success_probability" 
                name="Success Probability"
                tick={{ fontSize: 12 }}
                label={{ value: 'Success Probability (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'success_probability') return [`${value.toFixed(1)}%`, 'Success Probability'];
                  if (name === 'current_progress') return [`${value.toFixed(1)}%`, 'Current Progress'];
                  return [value, name];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0]) {
                    return `Workshop: ${payload[0].payload.title}`;
                  }
                  return '';
                }}
              />
              <Scatter 
                dataKey="success_probability" 
                fill="#3b82f6"
                r={6}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Resource Forecast */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Resource Demand Forecast</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.resource_forecast} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'mentor_demand') return [`${value}`, 'Mentor Demand'];
                  if (name === 'mentor_availability') return [`${value}`, 'Mentor Availability'];
                  if (name === 'workshop_capacity') return [`${value}`, 'Workshop Capacity'];
                  if (name === 'resource_gap') return [`${value}`, 'Resource Gap'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              />
              <Bar yAxisId="left" dataKey="mentor_demand" fill="#3b82f6" name="Mentor Demand" />
              <Bar yAxisId="left" dataKey="mentor_availability" fill="#10b981" name="Mentor Availability" />
              <Line yAxisId="right" type="monotone" dataKey="resource_gap" stroke="#ef4444" strokeWidth={3} name="Resource Gap" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Trends with Confidence Intervals */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Performance Trends & Predictions</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.performance_trends} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'actual_completion') return [`${value.toFixed(1)}%`, 'Actual Completion'];
                  if (name === 'predicted_completion') return [`${value.toFixed(1)}%`, 'Predicted Completion'];
                  if (name === 'confidence_interval_upper') return [`${value.toFixed(1)}%`, 'Upper Bound'];
                  if (name === 'confidence_interval_lower') return [`${value.toFixed(1)}%`, 'Lower Bound'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              />
              <Area
                type="monotone"
                dataKey="confidence_interval_upper"
                stackId="1"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.1}
                name="Confidence Interval"
              />
              <Area
                type="monotone"
                dataKey="confidence_interval_lower"
                stackId="1"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.1}
              />
              <Line
                type="monotone"
                dataKey="actual_completion"
                stroke="#10b981"
                strokeWidth={3}
                name="Actual Completion"
              />
              <Line
                type="monotone"
                dataKey="predicted_completion"
                stroke="#8b5cf6"
                strokeWidth={3}
                strokeDasharray="5 5"
                name="Predicted Completion"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Assessment and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Assessment */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Risk Assessment</h4>
          <div className="space-y-4">
            {/* Risk Distribution */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-600">{data.risk_assessment.high_risk_workshops}</div>
                <div className="text-xs text-red-600">High Risk</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-yellow-600">{data.risk_assessment.medium_risk_workshops}</div>
                <div className="text-xs text-yellow-600">Medium Risk</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{data.risk_assessment.low_risk_workshops}</div>
                <div className="text-xs text-green-600">Low Risk</div>
              </div>
            </div>

            {/* Critical Issues */}
            <div className="space-y-2">
              {data.risk_assessment.critical_issues.map((issue, index) => {
                const risk = getRiskLevel(issue.probability);
                return (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm font-medium text-gray-900">{issue.issue}</span>
                      <span className="text-xs px-2 py-1 rounded" style={{ 
                        backgroundColor: risk.color + '20', 
                        color: risk.color 
                      }}>
                        {risk.level}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      Impact: {issue.impact.toFixed(1)}% | Probability: {issue.probability.toFixed(1)}%
                    </div>
                    <div className="text-xs text-blue-600">
                      💡 {issue.mitigation}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Strategic Recommendations</h4>
          <div className="space-y-3">
            {data.recommendations.map((rec, index) => (
              <div key={index} className="p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-blue-900">{rec.category}</span>
                  <span className="text-xs px-2 py-1 rounded" style={{ 
                    backgroundColor: getPriorityColor(rec.priority) + '20', 
                    color: getPriorityColor(rec.priority) 
                  }}>
                    {rec.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
                <div className="text-sm text-blue-800 mb-2">{rec.recommendation}</div>
                <div className="flex justify-between text-xs text-blue-600">
                  <span>Impact: {rec.expected_impact.toFixed(1)}%</span>
                  <span>Effort: {rec.implementation_effort.toUpperCase()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalyticsDashboard;


